import { createRef, useEffect, useRef, useState } from "react";
import { FormInstance } from "antd";
import { PersonType } from "../types/PersonTypes";
import {
  Address,
  AddressRef,
  defaultSelectedAddress,
} from "../types/LocationTypes";
import { useServiceUserCreationCtx } from "../context/ServiceUserCreationContext";
import { PersonCreationData } from "../types/ServiceUserCreationTypes";
import { getInitialAddress } from "../helpers/personHelpers";

export type LabelTypes = {
  title: string;
  basicInfoLabel: string;
  isAgeOver18Label: string;
  address: {
    homeLabel: string;
    mailingLabel: string;
    cantFindAddressBtnTxt: string;
  };
  accommodations: {
    wheelChair: string;
    standardFoldableWheelchair: string;
    transferableWheelchair: string;
  };
};

export const usePersonForm = (
  form: FormInstance,
  personType: PersonType.SomeoneElse | PersonType.Myself
) => {
  const [shouldDisableBtn, setShouldDisableBtn] = useState<boolean>(true);
  const isAddCaregiverAsServiceUser = personType === PersonType.Myself;

  const renderLabels: Record<string, LabelTypes> = {
    [PersonType.SomeoneElse]: {
      title: "Scheduling Services for Someone Else",
      basicInfoLabel: "Who are you scheduling services for?",
      isAgeOver18Label: "Are they at least 18 years old?",
      address: {
        homeLabel: "What is their home address?",
        mailingLabel: "What is their mailing address?",
        cantFindAddressBtnTxt: "Can't find their address?",
      },
      accommodations: {
        wheelChair: "Does the service user travel with a wheelchair?",
        standardFoldableWheelchair:
          "Does the service user have a standard, foldable wheelchair?",
        transferableWheelchair:
          "Is the service user able to transfer from a wheelchair to a vehicle?",
      },
    },
    [PersonType.Myself]: {
      title: "Scheduling Services for Myself",
      basicInfoLabel: "Your Info",
      isAgeOver18Label: "Are you at least 18 years old?",
      address: {
        homeLabel: "What is your home address?",
        mailingLabel: "What is your mailing address?",
        cantFindAddressBtnTxt: "Can't find your address?",
      },
      accommodations: {
        wheelChair: "Do you travel with a wheelchair?",
        standardFoldableWheelchair:
          "Do you have a standard, foldable wheelchair?",
        transferableWheelchair:
          "Are you able to transfer from a wheelchair to a vehicle?",
      },
    },
  };

  const getFormFieldValues = () => {
    return form.getFieldsValue([
      "firstName",
      "lastName",
      "phone",
      "email",
      "relationship",
      "dateOfBirth",
      "homeAddress",
      "mailingAddress",
      "photo",
      "photoFile",
      "wandaAccommodations",
      "isAgeOver18",
    ]);
  };

  const validateAndCheckForm = () => {
    const {
      firstName,
      lastName,
      phone,
      relationship,
      homeAddress,
      mailingAddress,
      wandaAccommodations,
      isAgeOver18,
    } = getFormFieldValues();

    const isValidAge = isAgeOver18 === "Y";

    const isValidPhone =
      isAddCaregiverAsServiceUser || (phone && phone.length === 12);

    const isBasicInfoValid =
      isAddCaregiverAsServiceUser ||
      [firstName, lastName, relationship].every(Boolean);

    const validateAddressObject = (address: Address) => {
      const { address1, city, state, zip } = address;
      return [address1, city, state, zip].every(Boolean) && zip.length === 5;
    };

    const isValidHomeAddress = validateAddressObject(
      homeAddress || defaultSelectedAddress
    );
    const isValidMailingAddress = validateAddressObject(
      mailingAddress || defaultSelectedAddress
    );

    const isInvalidFieldExists = !(
      isValidAge &&
      isValidPhone &&
      isBasicInfoValid &&
      isValidHomeAddress &&
      isValidMailingAddress
    );

    isInvalidFieldExists
      ? setShouldDisableBtn(true)
      : setShouldDisableBtn(false);
  };

  return {
    labels: renderLabels[personType],
    isAddCaregiverAsServiceUser,
    shouldDisableBtn,
    validateAndCheckForm,
    getFormFieldValues,
  };
};
